import React, { useMemo, useState } from 'react';

import { IEquipmentProp } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/EquipmentProp.dto';

import { truncate } from '@r1-frontend/shared/helpers/truncate';

import * as ST from './styled';

const DEFAULT_DESCRIPTION_LENGTH = 170;

export interface IEquipPreview {
    className?: string,
    name?: string,
    images?: string[] | null,
    displayImage: string,
    equipProps?: IEquipmentProp[],
    description?: string,
    withDescTitle?: boolean,
}

interface IShortContent {
    contentLength: number,
    content: string,
}

const checkPropsHaveJSON = (props: IEquipmentProp[]) => {
    return props?.filter((prop) => prop?.value !== '["show"]').map((prop) => {
        let value = '';

        try {
            // Проверяем что в prop.value содержится JSON,
            // тогда берем уже вычисленное значение prop.computedValue
            const parsedProp = JSON.parse(prop?.value as string);
            if (parsedProp.length && prop.computedValue) {
                value = prop.computedValue;
            }
            if (prop.type === 'radio') {
                value = parsedProp ? 'Да' : 'Нет';
            }
        } catch {
            // Иначе берем значение prop.value как есть
            value = prop.value as string;
        }
        prop.computedValue = value?.toString();

        return { ... prop };
    });
};

const EquipPreview = ({
    className,
    images = [],
    displayImage,
    equipProps = [],
    description = '',
    withDescTitle = true,
} : IEquipPreview): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedImage, setSelectedImage] = useState(images?.length && images[0]);

    const filteredProps = useMemo(() => checkPropsHaveJSON(equipProps), [equipProps]);

    const shortContent = useMemo<IShortContent>(() => {
        let contentLength = 0;
        let content = '';

        if (description) {
            content = description.replace(/^(<.*?>)?(.*)(<\/.*?>)?$/, (_, _lt, _content, _rt) => {
                contentLength = _content.length;
                return `${_lt || ''}${truncate(_content, DEFAULT_DESCRIPTION_LENGTH)}${_rt || ''}`;
            });
        }

        return {
            contentLength,
            content,
        };
    }, [description]);

    const showMoreText = !filteredProps?.length ? 'Читать больше' : 'Подробнее';
    const isShowMore = shortContent.contentLength > DEFAULT_DESCRIPTION_LENGTH;

    return (
        <ST.Wrapper className={className}>
            <ST.PreviewBlock>
                <ST.ImageDisplay isFullWidth={!images?.length}>
                    <img src={selectedImage ? selectedImage : displayImage} alt='Продукт'/>
                </ST.ImageDisplay>
                {!!images?.length &&
                    <ST.ImagesList>
                        {images?.map((image, idx) => (
                            <ST.ImageItem
                                key={idx}
                                onClick={() => setSelectedImage(image)}
                                isSelected={selectedImage === image}
                            >
                                <img src={image} alt={`Картинка продукта ${idx}`}/>
                            </ST.ImageItem>
                        ))
                        }
                    </ST.ImagesList>
                }
            </ST.PreviewBlock>
            <ST.DescBlock>
                {withDescTitle && <ST.DescTitle>Коротко о товаре</ST.DescTitle>}
                {filteredProps.length > 0
                    ? <ST.SpecsList isExpanded={isExpanded}>
                        {filteredProps?.map((item, idx) => (
                            <ST.SpecItem key={idx}>
                                <ST.SpecRow><ST.SpecName title={item?.title}>{item?.title}</ST.SpecName></ST.SpecRow>
                                <ST.SpecRow><ST.SpecValue title={item?.computedValue}>{item?.computedValue}</ST.SpecValue></ST.SpecRow>
                            </ST.SpecItem>
                            ))
                        }
                    </ST.SpecsList>
                    : <ST.Text
                        isExpanded={isExpanded}
                        dangerouslySetInnerHTML={{ __html: isExpanded ? description : shortContent.content }}
                    />
                }
                {(!!filteredProps.length || isShowMore) &&
                    <ST.ShowMore isDesc={!!filteredProps.length} onClick={() => setIsExpanded(!isExpanded)}>
                        {!isExpanded ? showMoreText : 'Скрыть'}
                    </ST.ShowMore>
                }
            </ST.DescBlock>
        </ST.Wrapper>
    );
};

export default React.memo(EquipPreview);
